// src/services/redux/api/baseApi.js
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
// import { API_BASE_URL } from '../../config';
import {
  customerEndpoints,
  agencyEndpoints,
  AreaEndpoints,
  MotorAddOnMasterEndpoints,
  CRMGroupEndpoints,
  GroupEndpoints,
  branchEndpoints,
  authEndpoints,
  productMasterEndpoints,
  importFileEndpoints,
  themeMasterEndpoints,
  riderEndpoints,
  LifePolicyEntryEndpoints,
  PremiumDepositEndpoints,
  commissionReceiptEndpoints,
  fupUpdateEndpoints,
  policyReportEndpoints,
  insurerEndpoints,
  pdfImportEndpoints,
  commissionBillImportEndpoints,
  bonusCommissionEntryEndpoints,
  StateEndpoints,
  communicationMasterEndpoints,
  premiumCalendarEndpoints,
  errorTrackerEndpoints,
  VehicleMasterEndpoints,
  VehicleMakeMasterEndpoints,
  VehicleModelMasterEndpoints,
  VehicleClassMasterEndpoints,
  otherInsuranceEntryEndpoints,
  motorInsuranceEntryEndpoints,
  otherProductMasterEndpoints,
  motorProductEndpoints,
  healthProductEndpoints,
  employeeEndpoints,
  designationEndpoints,
  relationEndpoints,
  quotationEndpoints,
  productEndpoints,
  tpaMasterEndpoints,
  TitleEndpoints,
  healthProductBenefitEndpoints,
  documentEndpoints,
  healthInsuranceEntryEndpoints,
  policyNotReceivedEndpoints,
  consolidatedRenewalEndpoints,
  renewalIntimationEndpoints,
  giCommissionImportEndpoints,
  giCommissionReconciliationEndpoints,
  giCommissionEndpoints,
  giPolicyImportEndpoints,
  gicPolicyReportEndpoints,
  endorsementEndpoints,
  claimEndpoints,
  customTemplateEndpoints
} from './endpoints';


import { setCredentials, clearCredentials } from '../slices/authSlice';

// Core fetch with header preparation
const baseFetch = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  credentials: 'include', // IMPORTANT: Allow cookies to be sent with requests
  timeout: 1800000, // 30 minutes timeout for very large PDF generation
  prepareHeaders: (headers, { getState }) => {
    // Prefer token from Redux state
    const token = getState().auth?.token;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    headers.set('Accept', 'application/json');
    return headers;
  },
});

// Mutex to prevent multiple parallel refresh calls
let isRefreshing = false;
let refreshPromise = null;

// Wrapper to auto-handle 401 (attempts silent refresh before logout)
const baseQueryWithReauth = async (args, api, extraOptions) => {
  // 1. If not initialized (app start), try a silent refresh first to get a token
  if (!api.getState().auth?.isInitialized) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = baseFetch({ url: '/auth/refresh', method: 'POST' }, api, extraOptions);
      const refreshResult = await refreshPromise;
      isRefreshing = false;
      refreshPromise = null;

      if (refreshResult.data) {
        const { accessToken: token, user } = refreshResult.data;
        api.dispatch(setCredentials({ token, user }));
      } else {
        // If refresh fails on boot (e.g. no cookie), clear any stale user info from memory/localStorage
        api.dispatch(clearCredentials());
      }
    } else if (refreshPromise) {
      // If someone else is already refreshing on boot, wait for them
      await refreshPromise;
    }
  }

  // 2. Perform the actual request (if not a blocked protected route)
  const token = api.getState().auth?.token;
  const isAuthRoute = args.url?.includes('/auth/');

  if (api.getState().auth?.isInitialized && !token && !isAuthRoute) {
    // Return a local 401 if we know we don't have a token and it's not an auth route
    // This prevents hitting the server multiple times when unauthenticated
    return { error: { status: 401, data: { message: 'No token available' } } };
  }

  let result = await baseFetch(args, api, extraOptions);

  // 3. Handle 401 - Access Token Expired
  if (result?.error?.status === 401) {
    const state = api.getState();
    const token = state.auth?.token;

    // IMPORTANT: If we didn't have a token to begin with, 401 is expected for protected routes.
    // If we have a token and it's 401, it likely expired.
    if (token) {
      // Only attempt refresh if we're not already refreshing
      if (!isRefreshing) {
        isRefreshing = true;
        console.warn('[baseApi] 401 detected (expired token), starting silent refresh mutex...');

        refreshPromise = baseFetch({ url: '/auth/refresh', method: 'POST' }, api, extraOptions);
        const refreshResult = await refreshPromise;

        isRefreshing = false;
        refreshPromise = null;

        if (refreshResult.data) {
          console.log('[baseApi] Refresh successful, retrying original request.');
          const { accessToken: token, user } = refreshResult.data;
          api.dispatch(setCredentials({ token, user }));
          result = await baseFetch(args, api, extraOptions);
        } else {
          console.error('[baseApi] Refresh failed, logging out.');
          api.dispatch(clearCredentials());
          api.dispatch(baseApi.util.resetApiState());
        }
      } else if (refreshPromise) {
        // Wait for the existing refresh to finish
        console.warn('[baseApi] 401 detected, but refresh already in progress. Waiting...');
        await refreshPromise;

        // Check if it succeeded by seeing if we have a new token now
        const newToken = api.getState().auth?.token;
        if (newToken) {
          result = await baseFetch(args, api, extraOptions);
        } else {
          // Refresh failed for the other caller too
          api.dispatch(clearCredentials());
        }
      }
    } else {
      // If 401 and no token in state, we should probably just clear credentials 
      // to be safe (sync Redux with reality) and not retry anything.
      console.warn('[baseApi] 401 detected but no token found in state. User likely not logged in or session cleared.');
      if (state.auth?.user) {
        api.dispatch(clearCredentials());
      }
    }
  }

  return result;
};
// <-- function closed properly

// Create the base API slice

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,

  tagTypes: [
    'Customer',
    'Agency',
    'CRMGroup',
    'Area',
    'GroupMaster',
    'Branch',
    'Group',
    'Auth',
    'product',
    'theme',
    'LifePolicyEntry',
    'PremiumDepositEntry',
    'CommissionReceipt',
    'PendingPolicyImport',
    'Product',
    'Policy',
    'Theme',
    'policyReport',
    'insurer',
    'CommunicationTemplate',
    'ErrorTracker',
    'PremiumCalendar',
    'VehicleMaster',
    'VehicleMakeMaster',
    'VehicleModelMaster',
    'VehicleClassMaster',
    'OtherInsuranceEntry',
    'MotorInsuranceEntry',
    'OtherProductMaster',
    'OtherProducts',
    'MotorProduct',
    'HealthProduct',
    'HealthProductBenefit',
    'Title',
    'Relation',
    'Document',
    'PolicyNotReceived',
    'GICommission',
    'RenewalIntimation',
    'GICommissionImport',
    'GIPolicyImport',
    'GICPolicyReport',
    'Endorsement',
    'Claim',
    'CustomTemplate',
    'Comprehensive',
  ],

  endpoints: (builder) => ({
    ...customerEndpoints(builder),
    ...agencyEndpoints(builder),
    ...AreaEndpoints(builder),
    ...MotorAddOnMasterEndpoints(builder),
    ...CRMGroupEndpoints(builder),
    ...GroupEndpoints(builder),
    ...branchEndpoints(builder),
    ...authEndpoints(builder),
    ...productMasterEndpoints(builder),
    ...importFileEndpoints(builder),
    ...riderEndpoints(builder),
    ...themeMasterEndpoints(builder),
    ...LifePolicyEntryEndpoints(builder),
    ...otherInsuranceEntryEndpoints(builder),
    ...motorInsuranceEntryEndpoints(builder),
    ...otherProductMasterEndpoints(builder),
    ...PremiumDepositEndpoints(builder),
    ...commissionReceiptEndpoints(builder),
    ...fupUpdateEndpoints(builder),
    ...policyReportEndpoints(builder),
    ...premiumCalendarEndpoints(builder),
    ...insurerEndpoints(builder),
    ...pdfImportEndpoints(builder),
    ...commissionBillImportEndpoints(builder),
    ...bonusCommissionEntryEndpoints(builder),
    ...StateEndpoints(builder),
    ...communicationMasterEndpoints(builder),
    ...errorTrackerEndpoints(builder),
    ...customTemplateEndpoints(builder),
    ...VehicleMasterEndpoints(builder),
    ...VehicleMakeMasterEndpoints(builder),
    ...VehicleModelMasterEndpoints(builder),
    ...VehicleClassMasterEndpoints(builder),
    ...quotationEndpoints(builder),
    ...motorProductEndpoints(builder),
    ...healthProductEndpoints(builder),
    ...employeeEndpoints(builder),
    ...designationEndpoints(builder),
    ...relationEndpoints(builder),
    ...productEndpoints(builder),
    ...TitleEndpoints(builder),
    ...healthProductBenefitEndpoints(builder),
    ...tpaMasterEndpoints(builder),
    ...documentEndpoints(builder),
    ...healthInsuranceEntryEndpoints(builder),
    ...consolidatedRenewalEndpoints(builder),
    ...giCommissionEndpoints(builder),
    ...policyNotReceivedEndpoints(builder),
    ...renewalIntimationEndpoints(builder),
    ...giCommissionImportEndpoints(builder),
    ...giCommissionReconciliationEndpoints(builder),
    ...giPolicyImportEndpoints(builder),
    ...gicPolicyReportEndpoints(builder),
    ...endorsementEndpoints(builder),
    ...claimEndpoints(builder),
  }),
});

// Export hooks
export const {
  // Customer hooks
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomersWithoutAreaQuery,
  useLazyGetCustomersWithoutAreaQuery,
  useGetCustomerByIdQuery,
  useLazyGetCustomerByIdQuery,
  useSaveCustomerMutation,
  useSaveCustomerBankDetailsMutation,
  useDeleteCustomerMutation,
  useSearchCustomersMutation,
  useBulkDeleteCustomersMutation,
  useBulkUpdateCustomerAreaMutation,
  useGetPincodeDetailsQuery,
  useLazyGetPincodeDetailsQuery,
  useMergeAndDeleteCustomerMutation,

  // Agency hooks
  useGetAgenciesQuery,
  useGetAgencyByIdQuery,
  useSaveAgencyMutation,
  useUpdateAgencyMutation,
  useDeleteAgencyMutation,
  useBulkDeleteAgencyMutation,
  useGetAgencyPerformanceQuery,
  useGetAgencyAgentsQuery,

  // Area hooks
  useGetAreasQuery,
  useLazyGetAreasQuery,
  useGetAreaByIdQuery,
  useSaveAreaMutation,
  useUpdateAreaMutation,
  useDeleteAreaMutation,
  useBulkDeleteAreaMutation,
  useExportFailedPdfImportMutation,
  useLazyGetAreaByIdQuery,




  //Motor AddOn Master
  useGetMotorAddOnsQuery,
  useGetMotorAddOnByIdQuery,
  useSaveMotorAddOnMutation,
  useUpdateMotorAddOnMutation,
  useDeleteMotorAddOnMutation,
  useBulkDeleteMotorAddOnMutation,

  // State hooks
  useGetStatesQuery,

  // CRM Group hooks
  useGetCRMGroupsQuery,
  useGetCRMGroupByIdQuery,
  useSaveCRMGroupMutation,
  useUpdateCRMGroupMutation,
  useDeleteCRMGroupMutation,
  useBulkDeleteCRMGroupMutation,

  // Group hooks
  useGetGroupsQuery,
  useLazyGetGroupsQuery,
  useGetGroupByIdQuery,
  useLazyGetGroupByIdQuery,
  useSaveGroupMutation,
  useUpdateGroupMutation,
  useGetCustomerByGroupIdQuery,
  useLazyGetCustomerByGroupIdQuery,
  useGetGroupBankDetailsQuery,
  useLazyGetGroupBankDetailsQuery,
  useChangeGroupHeadMutation,
  useGenerateGroupCodeMutation,
  useCheckGroupCodeQuery,
  useLazyCheckGroupCodeQuery,
  useDeleteGroupMutation,
  useBulkDeleteGroupsMutation,
  useMergeAndDeleteGroupMutation,

  // Branch hooks
  useGetBranchesQuery,
  useLazyGetBranchesQuery,
  useGetBranchByIdQuery,
  useSaveBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
  useBulkDeleteBranchMutation,

  // Auth hooks
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useResetPasswordMutation,
  useForgotPasswordMutation,
  useVerifyOTPMutation,
  useResetPasswordOTPMutation,

  // product hooks
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductByIdQuery,
  useLazyGetProductByIdQuery,
  useSaveProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useBulkDeleteProductsMutation,
  useGetAllOptionsQuery,
  useLazyGetAllOptionsQuery,
  useGetAllFundsQuery,
  useLazyGetAllFundsQuery,

  // life policy entry hooks
  useGetLifePolicyEntryQuery,
  useLazyGetLifePolicyEntryQuery,
  useGetLifePolicyEntryByIdQuery,
  useLazyGetLifePolicyEntryByIdQuery,
  useSaveLifePolicyEntryMutation,
  useUpdateLifePolicyEntryMutation,
  useDeleteLifePolicyEntryMutation,
  useBulkDeleteLifePolicyEntryMutation,
  useLazyCheckPolicyExistsQuery,
  useCalculateBasicPremiumMutation,
  useGetValidTermsMutation,

  // premium deposit entry hooks
  useGetPremiumDepositEntryQuery,
  useGetPremiumDepositEntryByIdQuery,
  useSavePremiumDepositEntryMutation,
  useUpdatePremiumDepositEntryMutation,
  useDeletePremiumDepositEntryMutation,
  useBulkDeletePremiumDepositEntryMutation,
  useCalculatePremiumDepositLateFeeMutation,

  // commission receipt entry hooks
  useGetCommissionReceiptQuery,
  useGetCommissionReceiptByIdQuery,
  useSaveCommissionReceiptMutation,
  useUpdateCommissionReceiptMutation,
  useDeleteCommissionReceiptMutation,
  useBulkDeleteCommissionReceiptMutation,
  useCreateCommissionWithDepositMutation,

  // FUP / Policy hooks
  useGetFupPoliciesQuery,
  useBatchUpdateFupMutation,
  useDeletePolicyMutation,
  useBulkDeletePolicyMutation,

  // admin/theme hooks
  useGetThemesQuery,
  useCreateOrUpdateThemeMutation,
  useDeleteThemeMutation,

  useGetAvailableRidersQuery,
  useGetAllRidersQuery,
  useCalculateRiderPremiumMutation,

  // Add these near other exports:
  useGetPolicyReportQuery,
  useLazyGetPolicyReportQuery,
  usePreviewPolicyReportQuery,
  useLazyPreviewPolicyReportQuery,
  useExportPolicyReportMutation,
  useGetExportStatusQuery,
  useDownloadExportQuery,
  useLazyDownloadExportQuery,

  useGetPremiumDueReportQuery,
  useLazyGetPremiumDueReportQuery,
  usePreviewPremiumDueReportQuery,
  useLazyPreviewPremiumDueReportQuery,
  useExportPremiumDueReportMutation,
  useGetLapsedPoliciesReportQuery,
  useLazyGetLapsedPoliciesReportQuery,
  usePreviewLapsedPoliciesReportQuery,
  useLazyPreviewLapsedPoliciesReportQuery,
  useExportLapsedPoliciesReportMutation,
  useGetPremiumOutstandingReportQuery,
  useLazyGetPremiumOutstandingReportQuery,
  usePreviewPremiumOutstandingReportQuery,
  useLazyPreviewPremiumOutstandingReportQuery,
  useExportPremiumOutstandingReportMutation,

  useGetMaturityPoliciesReportQuery,
  useLazyGetMaturityPoliciesReportQuery,
  usePreviewMaturityPoliciesReportQuery,
  useLazyPreviewMaturityPoliciesReportQuery,
  useExportMaturityPoliciesReportExcelMutation,

  // Cash Flow Report
  useGetCashFlowReportQuery,
  usePreviewCashFlowReportQuery,
  useLazyPreviewCashFlowReportQuery,

  // Survival Benefit Report
  useGetSurvivalBenefitReportQuery,
  useLazyGetSurvivalBenefitReportQuery,
  usePreviewSurvivalBenefitReportQuery,
  useLazyPreviewSurvivalBenefitReportQuery,
  useExportSurvivalBenefitReportMutation,

  // Commission Summary Report
  useGetCommissionSummaryReportQuery,
  usePreviewCommissionSummaryReportQuery,
  useLazyPreviewCommissionSummaryReportQuery,

  useSendReportEmailMutation,

  // Comprehensive Insurance Report
  useGetComprehensiveReportQuery,
  useLazyGetComprehensiveReportQuery,
  usePreviewComprehensiveReportQuery,
  useLazyPreviewComprehensiveReportQuery,

  // Import-file hooks
  useUploadExcelMutation,
  useGetBatchesQuery,
  useValidateInsurersMutation,
  useValidateAgenciesMutation,
  useValidateProductsMutation,
  useValidateFupMutation,
  useValidateCustomersMutation,
  useGetInvalidInsurersMutation,
  useGetInvalidProductsMutation,
  useGetInvalidAgenciesMutation,
  useGetInvalidCustomersMutation,
  useGetInvalidFupMutation,
  useApplyCorrectionMutation,
  useFinishImportMutation,
  useLazyGetProductsByInsurerQuery,
  useGetImportSummaryQuery,
  useGetDuplicatePoliciesMutation,
  useGetImportStatusQuery,
  useLazyGetImportStatusQuery,

  // Pending Policy Import hooks
  useGetPendingPolicyImportListQuery,
  useGetPendingPolicyImportByIdQuery,
  useDeletePendingPolicyImportMutation,
  useBulkCreateCustomersMutation,
  useCheckExistingGroupsMutation,

  //Insurer hook
  useGetInsurersQuery,
  useLazyGetInsurersQuery,

  // PDF Import hooks
  useProcessPdfMutation,
  useImportPdfJsonMutation,

  // Commission Bill Import hooks
  useProcessCommissionBillPdfMutation,
  useImportCommissionBillJsonMutation,

  // Bonus Commission Entry hooks
  useCreateBonusCommissionEntryMutation,
  useGetBonusCommissionEntryListQuery,
  useGetBonusCommissionEntryByIdQuery,
  useDeleteBonusCommissionEntryMutation,
  useBulkDeleteBonusCommissionEntryMutation,
  useUpdateBonusCommissionEntryMutation,
  useGetPremiumCalendarReportQuery,
  useLazyGetPremiumCalendarReportQuery,
  useLazyPreviewPremiumCalendarReportQuery,

  // Communication Master hooks
  useGetCommunicationTemplatesQuery,
  useGetFilteredCustomersMutation,
  useSendCommunicationMutation,
  useSendPremiumCommunicationMutation,
  useGetBatchStatusQuery,
  useSaveCommunicationScheduleMutation,
  useGetCommunicationScheduleQuery,
  useDeleteCommunicationScheduleMutation,
  useSaveAllScheduleApiMutation,
  useGetCommunicationLogsQuery,
  useSendComparisonReportPdfMutation,

  //Other Product Master hooks
  useGetOtherProductsQuery,
  useDeleteOtherProductMutation,
  useSaveOtherProductMasterMutation,
  useGetOtherProductByIdQuery,

  //Quotation hooks
  useGetHealthQuotationListQuery,
  useDeleteHealthQuotationMutation,
  useGetHealthQuotationByIdQuery,
  useUpdateHealthQuotationMutation,
  useSubmitHealthQuotationMutation,
  useGetQuotationNoQuery,
  useGetZonesByProductQuery,
  useGetPlansByProductQuery,

  // Other Insurance Quotation hooks
  useGetOtherInsuranceEntryListQuery,
  useLazyGetOtherInsuranceEntryListQuery,
  useGetOtherInsuranceEntryByIdQuery,
  useSaveOtherInsuranceEntryMutation,
  useDeleteOtherInsuranceEntryMutation,
  useBulkDeleteOtherInsuranceEntryMutation,

  // Motor Insurance Entry hooks
  useGetMotorInsuranceEntryListQuery,
  useLazyGetMotorInsuranceEntryListQuery,
  useGetMotorInsuranceEntryByIdQuery,
  useGetMotorInsurancePremiumInfoQuery,
  useLazyGetMotorInsurancePremiumInfoQuery,
  useSaveMotorInsuranceEntryMutation,
  useDeleteMotorInsuranceEntryMutation,
  useBulkDeleteMotorInsuranceEntryMutation,
  useCheckMotorPolicyNumberExistsQuery,
  useLazyCheckMotorPolicyNumberExistsQuery,
  useGetIDVDepreciationRatesQuery,
  useGetRTOZoneQuery,
  useLazyGetRTOZoneQuery,

  // Other Insurance Quotation hooks
  useGetOtherInsuranceQuotationListQuery,
  useDeleteOtherInsuranceQuotationMutation,
  useSubmitOtherInsuranceQuotationMutation,
  useGetOtherInsuranceQuotationByIdQuery,
  useUpdateOtherInsuranceQuotationMutation,

  useGetOtherProductMasterListQuery,
  useLazyGetOtherProductMasterListQuery,
  useGetOtherProductMasterByIdQuery,
  useDeleteOtherProductMasterMutation,

  // Health Insurance Entry hooks
  useGetHealthInsuranceEntryListQuery,
  useGetHealthInsuranceEntryByIdQuery,
  useSaveHealthInsuranceEntryMutation,
  useDeleteHealthInsuranceEntryMutation,
  useBulkDeleteHealthInsuranceEntryMutation,
  useCheckHealthPolicyNumberExistsQuery,
  useLazyCheckHealthPolicyNumberExistsQuery,
  useSearchPolicyByNoQuery,
  useLazySearchPolicyByNoQuery,
  useVerifyAdminPasswordMutation,

  // Policy Not Received hooks
  useGetPolicyNotReceivedListQuery,
  useLazyGetPolicyNotReceivedListQuery,

  // Renewal Intimation hooks
  useGetRenewalIntimationListQuery,
  useLazyGetRenewalIntimationListQuery,
  useSendRenewalCommunicationMutation,

  //Employee Master hooks
  useSaveEmployeeMutation,
  useUpdateEmployeeMutation,
  useGetEmployeeListQuery,
  useLazyGetEmployeeListQuery,
  useGetEmployeeByIdQuery,
  useDeleteEmployeeMutation,

  // Designation Master hooks
  useGetDesignationListQuery,
  useSaveDesignationMutation,
  useDeleteDesignationMutation,

  // Relation Master hooks
  useGetRelationListQuery,

  //Vehicle Model Master hooks
  useGetVehicleModelMastersQuery,
  useLazyGetVehicleModelMastersQuery,
  useGetVehicleModelByIdQuery,
  useSaveVehicleModelMutation,
  useUpdateVehicleModelMutation,
  useDeleteVehicleModelMutation,
  useBulkDeleteVehicleModelMutation,

  //Vehicle Master hooks
  useGetVehicleMastersQuery,
  useLazyGetVehicleMastersQuery,
  useGetVehicleMasterByIdQuery,
  useSaveVehicleMasterMutation,
  useUpdateVehicleMasterMutation,
  useDeleteVehicleMasterMutation,
  useBulkDeleteVehicleMasterMutation,
  // Cascading dropdowns (Make → Model → Variant)
  useGetVehicleModelsForDropdownQuery,
  useLazyGetVehicleModelsForDropdownQuery,
  useGetVehicleVariantsForDropdownQuery,
  useLazyGetVehicleVariantsForDropdownQuery,

  //Vehicle Class Master hooks
  useGetVehicleClassesForDropdownQuery,
  useLazyGetVehicleClassesForDropdownQuery,
  useGetVehicleLoadOrDiscountsQuery,
  useLazyGetVehicleLoadOrDiscountsQuery,

  //Vehicle Make Master hooks
  useGetVehicleMakeMastersQuery,
  useLazyGetVehicleMakeMastersQuery,
  useGetVehicleMakeByIdQuery,
  useSaveVehicleMakeMutation,
  useUpdateVehicleMakeMutation,
  useDeleteVehicleMakeMutation,
  useBulkDeleteVehicleMakeMutation,
  useGetVehicleMakesForDropdownQuery,
  useLazyGetVehicleMakesForDropdownQuery,

  //Motor Product hooks
  useGetMotorProductsQuery,
  useGetMotorProductByIdQuery,
  useSaveMotorProductMutation,
  useUpdateMotorProductMutation,
  useDeleteMotorProductMutation,
  useBulkDeleteMotorProductMutation,

  //Health Product hooks
  useGetHealthProductsQuery,
  useGetHealthProductByIdQuery,
  useSaveHealthProductMutation,
  useUpdateHealthProductMutation,
  useDeleteHealthProductMutation,
  useBulkDeleteHealthProductMutation,
  useGetProductsByTypeQuery,

  // TPA Master hooks
  useGetTPAMastersQuery,
  useGetTitlesQuery,
  useLazyGetTitlesQuery,
  useGetTitleByIdQuery,
  useGetHealthBenefitsQuery,
  useGetHealthComparisonQuery,
  useLazyGetHealthComparisonQuery,
  useSaveHealthProductBenefitMappingMutation,
  useGetDocumentsByModuleQuery,
  useGetUploadedDocumentsQuery,
  useUploadDocumentMutation,

  // Error Tracker hooks
  useGetErrorTrackerQuery,
  useResolveErrorMutation,
  useCheckExistingPoliciesMutation,
  useCheckPlansMutation,
  useCheckBranchesMutation,
  useSendProductRequestMutation,
  useSendCustomerRequestMutation,
  useGetConsolidatedRenewalListQuery,
  useLazyGetConsolidatedRenewalListQuery,

  // GI Commission Import hooks
  useSaveGICommissionBatchMutation,
  useGetGICommissionBatchesQuery,
  useDeleteGICommissionBatchMutation,

  // GI Policy Import hooks
  useUploadGIExcelMutation,
  useGetGIBatchesQuery,
  useValidateGIInsurersMutation,
  useValidateGIAgenciesMutation,
  useValidateGIProductsMutation,
  useValidateGICustomersMutation,
  useValidateGIFupMutation,
  useValidateGIZonesMutation,
  useValidateGIPlansMutation,
  useGetGIInvalidInsurersMutation,
  useGetGIInvalidProductsMutation,
  useGetGIInvalidAgenciesMutation,
  useGetGIInvalidCustomersMutation,
  useGetGIInvalidFupMutation,
  useGetGIInvalidZonesMutation,
  useGetGIInvalidPlansMutation,
  useApplyGICorrectionMutation,
  useBulkCreateGICustomersAndApplyCorrectionMutation,
  useFinishGIImportMutation,
  useGetGIImportSummaryQuery,
  useGetGIDuplicatePoliciesMutation,
  useGetGIMotorImportPreviewMutation,
  useValidateGIVehiclesMutation,
  useGetGIInvalidVehiclesMutation,
  useGetGIActiveVehiclesQuery,

  // GI Commission Reconciliation hooks
  useGetGICommissionReconciliationListQuery,
  useLazyGetGICommissionReconciliationListQuery,
  useGetGICommissionOutstandingListQuery,
  useLazyGetGICommissionOutstandingListQuery,
  useReconcilePoliciesMutation,


  useGetGICommissionsQuery,
  useGetGICommissionDetailsQuery,
  useLazyGetGICommissionDetailsQuery,
  useSaveGICommissionMutation,
  useDeleteGICommissionMutation,
  useGetGICommissionRateQuery,
  useLazyGetGICommissionRateQuery,

  // GIC Policy Report hooks
  useGetGICPolicyReportQuery,
  useLazyGetGICPolicyReportQuery,
  useLazyPreviewGICPolicyReportQuery,
  useExportGICPolicyReportMutation,
  useGetGICExportStatusQuery,

  // Endorsement hooks
  // -- ENDORSEMENT ENDPOINTS --
  useGetEndorsementsByPolicyQuery,
  useGetEndorsementByIdQuery,
  useSaveEndorsementMutation,
  useDeleteEndorsementMutation,

  // -- ENDORSEMENT INTIMATION ENDPOINTS --
  useGetPoliciesByCustomerQuery,
  useLazyGetPoliciesByCustomerQuery,
  useSendEndorsementIntimationMutation,

  // Claims hooks
  useGetClaimsByPolicyQuery,
  useLazyGetClaimsByPolicyQuery,
  useGetClaimByIdQuery,
  useSaveClaimMutation,
  useDeleteClaimMutation,

  // Custom Template hooks
  useGetCustomTemplatesQuery,
  useSaveCustomTemplateMutation,
  useDeleteCustomTemplateMutation
} = baseApi;
